import React, { useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Collapse,
  Chip,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PokemonSprite from "./PokemonSprite";

// ── Win bar ────────────────────────────────────────────────────────────
function winColor(prob) {
  const pct = prob * 100;
  if (pct >= 60) return "#4ade80";
  if (pct >= 40) return "#facc15";
  return "#f87171";
}

function WinBar({ winP1, winP2, compact = false }) {
  if (winP1 === null || winP2 === null) return null;
  const p1Pct = (winP1 * 100).toFixed(1);
  const p2Pct = (winP2 * 100).toFixed(1);
  const p1Color = winColor(winP1);

  if (compact) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: { xs: 0, sm: 180 }, width: { xs: "100%", sm: "auto" } }}>
        <Typography variant="caption" sx={{ color: p1Color, fontWeight: 700, minWidth: { xs: 34, sm: 38 }, textAlign: "right" }}>
          {p1Pct}%
        </Typography>
        <Box sx={{ flex: 1, height: 6, borderRadius: 3, overflow: "hidden", bgcolor: "rgba(255,255,255,0.12)" }}>
          <Box sx={{ height: "100%", width: `${winP1 * 100}%`, bgcolor: winColor(winP1), borderRadius: 3, transition: "width 0.4s ease" }} />
        </Box>
        <Typography variant="caption" sx={{ color: winColor(winP2), fontWeight: 700, minWidth: { xs: 34, sm: 38 } }}>
          {p2Pct}%
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
        <Typography variant="caption" sx={{ color: p1Color, fontWeight: 700 }}>P1 {p1Pct}%</Typography>
        <Typography variant="caption" sx={{ color: winColor(winP2), fontWeight: 700 }}>P2 {p2Pct}%</Typography>
      </Box>
      <Box sx={{ height: 8, borderRadius: 4, overflow: "hidden", bgcolor: "rgba(255,255,255,0.12)" }}>
        <Box sx={{ height: "100%", width: `${winP1 * 100}%`, bgcolor: winColor(winP1), borderRadius: 4, transition: "width 0.4s ease" }} />
      </Box>
    </Box>
  );
}

// ── Delta badge ────────────────────────────────────────────────────────
function Delta({ delta }) {
  if (delta === undefined || delta === null) return null;
  const pct = (delta * 100).toFixed(1);
  const positive = delta > 0.005;
  const negative = delta < -0.005;
  const color = positive ? "#4ade80" : negative ? "#f87171" : "rgba(255,255,255,0.4)";
  return (
    <Typography variant="caption" sx={{ color, fontWeight: 700, minWidth: 46, textAlign: "right" }}>
      {positive ? "+" : ""}{pct}%
    </Typography>
  );
}

// ── Single combo row ───────────────────────────────────────────────────
function ComboRow({ combo }) {
  return (
    <Box sx={{
      display: "flex", alignItems: "flex-start", gap: 1.5, py: 0.75, px: 1,
      borderRadius: 1, bgcolor: "rgba(255,255,255,0.05)",
      "&:hover": { bgcolor: "rgba(255,255,255,0.09)" },
    }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.92)", display: "block" }}>
          <strong>{combo.pokemon1}</strong>: {combo.move1}
          {combo.target1 && combo.target1 !== combo.move1 && combo.target1 !== `switch to ${combo.pokemon1}` && (
            <span style={{ color: "rgba(255,255,255,0.45)" }}> → {combo.target1}</span>
          )}
        </Typography>
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.92)", display: "block" }}>
          <strong>{combo.pokemon2}</strong>: {combo.move2}
          {combo.target2 && combo.target2 !== combo.move2 && combo.target2 !== `switch to ${combo.pokemon2}` && (
            <span style={{ color: "rgba(255,255,255,0.45)" }}> → {combo.target2}</span>
          )}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, pt: 0.5 }}>
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", minWidth: 36, textAlign: "right" }}>
          {combo.winRate?.toFixed(1)}%
        </Typography>
        <Delta delta={combo.delta} />
      </Box>
    </Box>
  );
}

// ── Status helpers ─────────────────────────────────────────────────────
const STATUS_LABELS = {
  paralyzed: { label: "PAR", color: "#facc15" },
  burned:    { label: "BRN", color: "#f97316" },
  asleep:    { label: "SLP", color: "#a78bfa" },
  frozen:    { label: "FRZ", color: "#67e8f9" },
  poisoned:  { label: "PSN", color: "#c084fc" },
  "badly poisoned": { label: "TOX", color: "#a855f7" },
};

const BOOST_LABELS = { atk: "Atk", def: "Def", spa: "SpA", spd: "SpD", spe: "Spe", acc: "Acc", eva: "Eva" };

function volatileLabel(name) {
  const n = name.toLowerCase();
  if (n.startsWith("protosynthesis")) return `Protosyn(${n.replace("protosynthesis", "").toUpperCase() || "?"})`;
  if (n.startsWith("quarkdrive"))     return `Quark(${n.replace("quarkdrive", "").toUpperCase() || "?"})`;
  if (n === "taunt")    return "Taunt";
  if (n === "encore")   return "Encore";
  if (n === "leechseed") return "Leech";
  if (n === "confusion") return "Conf";
  if (n === "partiallytrapped") return "Trap";
  return null; // skip unknown/irrelevant ones
}

function PokemonBadges({ p }) {
  const badges = [];

  // Tera
  if (p.tera) badges.push(
    <Box key="tera" sx={{ px: 0.6, py: 0.1, borderRadius: 0.5, bgcolor: "rgba(250,204,21,0.25)", border: "1px solid #facc15", fontSize: "0.6rem", color: "#facc15", lineHeight: 1.4, whiteSpace: "nowrap" }}>
      ◈ {p.tera}
    </Box>
  );

  // Non-volatile status
  if (p.status) {
    const s = STATUS_LABELS[p.status] || { label: p.status.toUpperCase().slice(0, 3), color: "#94a3b8" };
    badges.push(
      <Box key="status" sx={{ px: 0.6, py: 0.1, borderRadius: 0.5, bgcolor: `${s.color}22`, border: `1px solid ${s.color}`, fontSize: "0.6rem", color: s.color, lineHeight: 1.4 }}>
        {s.label}
      </Box>
    );
  }

  // Volatile statuses
  (p.volatile || []).forEach((v, i) => {
    const lbl = volatileLabel(v);
    if (!lbl) return;
    badges.push(
      <Box key={`v${i}`} sx={{ px: 0.6, py: 0.1, borderRadius: 0.5, bgcolor: "rgba(139,92,246,0.2)", border: "1px solid rgba(139,92,246,0.5)", fontSize: "0.6rem", color: "#a78bfa", lineHeight: 1.4, whiteSpace: "nowrap" }}>
        {lbl}
      </Box>
    );
  });

  // Stat boosts
  const boosts = p.boosts || {};
  Object.entries(BOOST_LABELS).forEach(([key, name]) => {
    const val = boosts[key];
    if (!val || val === 0) return;
    const positive = val > 0;
    badges.push(
      <Box key={key} sx={{ px: 0.6, py: 0.1, borderRadius: 0.5, bgcolor: positive ? "rgba(74,222,128,0.15)" : "rgba(248,113,113,0.15)", border: `1px solid ${positive ? "#4ade80" : "#f87171"}`, fontSize: "0.6rem", color: positive ? "#4ade80" : "#f87171", lineHeight: 1.4, whiteSpace: "nowrap" }}>
        {positive ? "+" : ""}{val} {name}
      </Box>
    );
  });

  if (!badges.length) return null;
  return <Box sx={{ display: "flex", gap: 0.4, flexWrap: "wrap", mt: 0.3 }}>{badges}</Box>;
}

// ── Revealed Pokémon row ───────────────────────────────────────────────
function RevealedRow({ label, pokemon }) {
  if (!pokemon?.length) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, flexWrap: "wrap" }}>
      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", minWidth: 20, pt: 0.5 }}>{label}</Typography>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        {pokemon.map((p, i) => (
          <Box key={i} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <PokemonSprite pokemon={{ name: p.name }} fainted={p.fainted} />
            <PokemonBadges p={p} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ── Main TurnCard ──────────────────────────────────────────────────────
const TurnCard = ({ turn, p1 = "P1", p2 = "P2" }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  const formattedMoveP1 = turn.moveUsedP1?.replace(/^,\s*/, "").trim() || "—";
  const formattedMoveP2 = turn.moveUsedP2?.replace(/^,\s*/, "").trim() || "—";

  const topCombos   = [...(turn.combinations   || [])].sort((a, b) => (b.delta ?? 0) - (a.delta ?? 0)).slice(0, 6);
  const topCombosP2 = [...(turn.combinationsP2 || [])].sort((a, b) => (b.delta ?? 0) - (a.delta ?? 0)).slice(0, 6);
  const hasCombos   = topCombos.length > 0 || topCombosP2.length > 0;

  const hasRevealedP1 = (turn.revealed?.p1 || []).length > 0;
  const hasRevealedP2 = (turn.revealed?.p2 || []).length > 0;

  const weather = turn.state?.weather;
  const field   = turn.state?.field;
  const room    = turn.state?.room;

  return (
    <Box
      sx={{
        bgcolor: theme.palette.primary.main,
        borderRadius: 2,
        overflow: "hidden",
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: 6 },
      }}
    >
      {/* ── Collapsed header row ── */}
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: "flex", alignItems: "center", gap: { xs: 1.25, sm: 2 }, px: 2, py: 1.25,
          flexWrap: { xs: "wrap", sm: "nowrap" },
          cursor: "pointer", userSelect: "none",
          "&:hover": { bgcolor: "rgba(255,255,255,0.04)" },
        }}
      >
        {/* Turn badge */}
        <Typography
          component="h2"
          sx={{ fontWeight: 700, fontSize: "0.85rem", color: "white", minWidth: { xs: "auto", sm: 54 }, flexShrink: 0 }}
        >
          Turn {turn.turn_number}
        </Typography>

        <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.12)", display: { xs: "none", sm: "block" } }} />

        {/* P1 active */}
        <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0, order: { xs: 2, sm: 0 } }}>
          {(turn.activePokemon?.p1 || []).filter(Boolean).map((name, i) => (
            <PokemonSprite key={i} pokemon={{ name }} />
          ))}
        </Stack>

        {/* Mobile VS separator */}
        <Typography
          variant="caption"
          sx={{
            display: { xs: "block", sm: "none" },
            order: { xs: 3, sm: 0 },
            fontWeight: 700,
            color: "rgba(255,255,255,0.55)",
            letterSpacing: 0.5,
            textTransform: "lowercase",
            lineHeight: 1,
          }}
        >
          vs
        </Typography>

        {/* Win bar (compact) — center */}
  <Box sx={{ flex: { xs: "1 1 100%", sm: 1 }, order: { xs: 5, sm: 0 }, display: "flex", justifyContent: "center", mt: { xs: 0.25, sm: 0 } }}>
          {!turn.noData ? (
            <WinBar winP1={turn.winProbP1} winP2={turn.winProbP2} compact />
          ) : (
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.3)", fontStyle: "italic" }}>
              No prediction
            </Typography>
          )}
        </Box>

        {/* P2 active */}
        <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0, order: { xs: 4, sm: 0 } }}>
          {(turn.activePokemon?.p2 || []).filter(Boolean).map((name, i) => (
            <PokemonSprite key={i} pokemon={{ name }} />
          ))}
        </Stack>

        {/* Chevron */}
        <Box sx={{ color: "rgba(255,255,255,0.4)", display: "flex", flexShrink: 0, order: { xs: 1, sm: 0 }, ml: "auto" }}>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </Box>
      </Box>

      {/* ── Expanded content ── */}
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
        <Box sx={{ px: 2, py: 1.5, display: "flex", flexDirection: "column", gap: 2 }}>

          {/* Moves done */}
          <Box>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, fontSize: "0.62rem", display: "block", mb: 0.75 }}>
              Moves played
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
              <span style={{ color: "rgba(255,255,255,0.45)" }}>{p1} </span>{formattedMoveP1}
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)", mt: 0.25 }}>
              <span style={{ color: "rgba(255,255,255,0.45)" }}>{p2} </span>{formattedMoveP2}
            </Typography>
          </Box>

          {/* Revealed / fainted Pokémon */}
          {(hasRevealedP1 || hasRevealedP2) && (
            <Box>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, fontSize: "0.62rem", display: "block", mb: 0.75 }}>
                Revealed Pokémon
              </Typography>
              <Stack spacing={0.75}>
                <RevealedRow label={p1} pokemon={turn.revealed?.p1} />
                <RevealedRow label={p2} pokemon={turn.revealed?.p2} />
              </Stack>
            </Box>
          )}

          {/* Condition chips */}
          {(weather || field || room) && (
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5 }}>
              {weather && <Chip label={`☁ ${weather}`}  size="small" sx={{ bgcolor: "rgba(255,255,255,0.1)", color: "white", fontSize: "0.65rem" }} />}
              {field   && <Chip label={`⬡ ${field}`}    size="small" sx={{ bgcolor: "rgba(255,255,255,0.1)", color: "white", fontSize: "0.65rem" }} />}
              {room    && <Chip label={`◈ ${room}`}      size="small" sx={{ bgcolor: "rgba(255,255,255,0.1)", color: "white", fontSize: "0.65rem" }} />}
            </Stack>
          )}

          {/* AI Recommendations */}
          {hasCombos && (
            <Box>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, fontSize: "0.62rem", display: "block", mb: 0.75 }}>
                Recommendations
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {topCombos.length > 0 && (
                  <Box sx={{ flex: 1, minWidth: 260 }}>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.3)", display: "block", mb: 0.5 }}>{p1} best plays</Typography>
                    <Stack spacing={0.5}>
                      {topCombos.map((c, i) => <ComboRow key={i} combo={c} />)}
                    </Stack>
                  </Box>
                )}
                {topCombosP2.length > 0 && (
                  <Box sx={{ flex: 1, minWidth: 260 }}>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.3)", display: "block", mb: 0.5 }}>{p2} best plays</Typography>
                    <Stack spacing={0.5}>
                      {topCombosP2.map((c, i) => <ComboRow key={i} combo={c} />)}
                    </Stack>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {!hasCombos && !turn.noData && (
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.3)", fontStyle: "italic" }}>
              No AI recommendations for this turn
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default TurnCard;
